import BenchmarkValue from "@/components/BenchmarkValue";
import { isQualitative } from "@/lib/qualitativeRubric";
import BenchmarkUploadHelp, { benchmarkUploadIssues } from "@/components/BenchmarkUploadHelp";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { API_BASE_URL } from "../config/api";
import AdminGuard, { useCanEdit } from "@/components/AdminGuard";
import ProductTypeManager from "@/components/ProductTypeManager";
import { TOSS_LOGO_URL as LOGO_URL, BRAND } from "@/lib/branding";
import { PARAM_UNITS, advancedUnit } from "@/lib/reportUtils";
import ReferenceBasis, { basisUnit } from "@/components/ReferenceBasis";

const API = API_BASE_URL;

const PARAM_KEYS = [
  "gwp", "water", "pollution", "circularity", "time", "cost", "risk", "social", "ecosystem", "economic", "certifications", "governance"
];
const PARAM_LABELS = {
  gwp: "GWP", water: "Water consumption", pollution: "Pollution points", circularity: "Recycled content",
  risk: "Risk", social: "Social", ecosystem: "Ecosystem", economic: "Economic",
  certifications: "Certs", time: "Time", cost: "Cost", governance: "Gov"
};

const SUBSTAGES = [
  "manufacturing_pre_processing", "manufacturing_production",
  "use_phase_operation", "use_phase_maintenance",
  "eol_recycling", "eol_recovery",
  "logistics_transportation", "logistics_storage"
];
const SUBSTAGE_LABELS = {
  manufacturing_pre_processing: "Mfg Pre-Processing", manufacturing_production: "Mfg Production",
  use_phase_operation: "Use - Operation", use_phase_maintenance: "Use - Maintenance",
  eol_recycling: "EOL - Recycling", eol_recovery: "EOL - Recovery",
  logistics_transportation: "Logistics - Transport", logistics_storage: "Logistics - Storage"
};

export default function AdminBenchmarks() {
  const [productTypes, setProductTypes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedPT, setSelectedPT] = useState("");
  const [benchmarks, setBenchmarks] = useState({});
  const [importIssues, setImportIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);
  const canEdit = useCanEdit();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {


      const [ptRes, matRes] = await Promise.all([
        axios.get(`${API}/product-types`),
        axios.get(`${API}/materials`)
      ]);
      setProductTypes(ptRes.data);
      setMaterials(matRes.data);
      if (ptRes.data.length > 0 && !selectedPT) {
        setSelectedPT(ptRes.data[0].id);
        await loadBenchmarks(ptRes.data[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const refreshProductTypes = async () => {
    try {
      const ptRes = await axios.get(`${API}/product-types`);
      setProductTypes(ptRes.data);
    } catch (e) { console.error(e); }
  };

  const loadBenchmarks = async (ptId) => {
    if (!ptId) { setBenchmarks({}); return; }
    try {
      const res = await axios.get(`${API}/product-benchmarks/${ptId}`);
      setBenchmarks(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSelectPT = (ptId) => {
    setSelectedPT(ptId);
    loadBenchmarks(ptId);
  };

  const updateBenchmarkVal = (substage, type, param, value) => {
    setBenchmarks(prev => {
      const next={...prev,[substage]:{...prev[substage],best:{...prev[substage]?.best},worst:{...prev[substage]?.worst}}};
      if(value==='not_applicable') { next[substage].best[param]=value;next[substage].worst[param]=value; }
      else {
        if(next[substage].best[param]==='not_applicable' || next[substage].worst[param]==='not_applicable') { next[substage].best[param]=null;next[substage].worst[param]=null; }
        next[substage][type][param]=value;
      }
      return next;
    });
  };

  const saveBenchmark = async (substage) => {
    try {
      await axios.put(`${API}/product-benchmarks/${selectedPT}`, {
        substage,
        best: benchmarks[substage]?.best || {},
        worst: benchmarks[substage]?.worst || {},
        units: PARAM_UNITS,
        reference: {basis:"per_assessed_product"},
        methods: {},
      });
      toast.success(`Saved ${SUBSTAGE_LABELS[substage]}`);
    } catch (e) { const d=e.response?.data?.detail; toast.error(d?.issues?.map(i=>`${i.location}: ${i.message}`).join(' ') || (typeof d === 'string' ? d : "Save failed")); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-benchmarks">
      <div className="flex items-center gap-3 mb-2">
        <img src={LOGO_URL} alt={BRAND.short} className="h-8 w-auto object-contain" />
        <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Product Benchmarks</h1>
      </div>
      <p className="text-sm text-[#617364] mb-4">Enter Best and Worst values per one product. Percentages and duration retain their own units. Leave a pair blank or select Not applicable to hide that product input. Rubric parameters stay active unless marked Not applicable.</p>
      <AdminGuard resource="product benchmarks" /><BenchmarkUploadHelp issues={importIssues} product />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border-[#E0E6E1] bg-white">
          <CardHeader><CardTitle className="text-base font-medium text-[#1B291D]">Materials</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {materials.map(m => (
                <Badge key={m.name} variant="outline" className="border-[#E0E6E1] text-[#617364] text-xs">{m.name}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="md:col-span-2">
          <ProductTypeManager
            apiBase={API}
            productTypes={productTypes}
            canEdit={canEdit}
            onChanged={refreshProductTypes}
            selectedPT={selectedPT}
            onSelectedChange={handleSelectPT}
            modeLabel="ADVANCED"
          />
        </div>
      </div>

      {productTypes.length > 0 && selectedPT && (
        <Card className="border-[#E0E6E1] bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>
                Product Benchmarks
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={selectedPT} onValueChange={handleSelectPT}>
                  <SelectTrigger className="w-[200px] border-[#E0E6E1]" data-testid="select-pt">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {productTypes.map(pt => (
                      <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <input type="file" ref={fileRef} accept=".xlsx" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !selectedPT) return;
                setImportIssues([]);
                const fd = new FormData(); fd.append("file", file);
                try {
                  const res = await axios.post(`${API}/import-product-benchmarks/${selectedPT}`, fd);
                  toast.success(`Imported ${res.data.count} benchmark records`);
                  await loadBenchmarks(selectedPT);
                } catch (err) { setImportIssues(benchmarkUploadIssues(err)); toast.error("Upload not saved. See the field details below."); }
                if (fileRef.current) fileRef.current.value = "";
              }} className="hidden" />
              <Button variant="outline" size="sm" onClick={() => window.open(`${API}/template/product-benchmarks`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="download-pb-template">
                <Download className="w-3.5 h-3.5 mr-1" /> Guided Excel template
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]" data-testid="import-pb-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> Import Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={SUBSTAGES[0]}>
              <TabsList className="bg-[#F8F9F8] border border-[#E0E6E1] p-1 mb-4 flex-wrap h-auto">
                {SUBSTAGES.map(ss => (
                  <TabsTrigger key={ss} value={ss} className="data-[state=active]:bg-white data-[state=active]:text-[#43A047] text-xs" data-testid={`bm-tab-${ss}`}>
                    {SUBSTAGE_LABELS[ss]}
                  </TabsTrigger>
                ))}
              </TabsList>
              {SUBSTAGES.map(ss => (
                <TabsContent key={ss} value={ss}>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E0E6E1]">
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-[#617364] font-bold">Parameter</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-[#617364] font-bold">Best</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-[#617364] font-bold">Worst</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {PARAM_KEYS.map(pk => (
                        <TableRow key={pk} className="border-[#E0E6E1]">
                          <TableCell className="text-sm font-medium text-[#1B291D]">{PARAM_LABELS[pk]}<span className="block text-xs font-normal">{advancedUnit(basisUnit(pk,benchmarks[ss]?.reference) || benchmarks[ss]?.units?.[pk] || PARAM_UNITS[pk])}</span>
                          </TableCell>
                          <TableCell colSpan={isQualitative(pk) ? 2 : 1}>
                            <BenchmarkValue disabled={!canEdit} rubric={isQualitative(pk)} value={benchmarks[ss]?.best?.[pk]} onChange={value=>updateBenchmarkVal(ss,"best",pk,value)} label={`${PARAM_LABELS[pk]} Best`} testId={`bm-best-${ss}-${pk}`} />
                          </TableCell>
                          {!isQualitative(pk) && <TableCell>
                            <BenchmarkValue disabled={!canEdit} value={benchmarks[ss]?.worst?.[pk]} onChange={value=>updateBenchmarkVal(ss,"worst",pk,value)} label={`${PARAM_LABELS[pk]} Worst`} testId={`bm-worst-${ss}-${pk}`} />
                          </TableCell>}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="mt-4 flex justify-end">
                    <Button onClick={() => saveBenchmark(ss)} disabled={!canEdit} className="bg-[#43A047] hover:bg-[#388E3C] text-white disabled:opacity-50" data-testid={`save-bm-${ss}`} title={!canEdit ? "Admin access required" : ""}>
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
}